// =============================================================================
// PAGE AGENT CONFORMITE - Extraction IA des dossiers clients
// =============================================================================
import { useState, useEffect, useCallback } from "react";
import {
  Bot,
  Play,
  Upload,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  FileText,
  Users,
  Mail,
  Phone,
  TrendingUp,
  Zap,
  Search,
  ChevronDown,
  ChevronUp,
  FolderOpen,
  MessageSquare,
  LayoutDashboard,
  ScanSearch,
  CalendarClock,
  Clock,
  StopCircle,
  History,
  Layers,
  Timer,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { getInstallateurDisplayName } from "../../utils/installateurDisplay";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import ConversationPanel from "./ConversationPanel";
import ChatLibrePanel from "./ChatLibrePanel";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

function getAuthHeaders(tenantId) {
  const token = localStorage.getItem("session_token");
  return {
    "Content-Type": "application/json",
    ...enteteTenant(tenantId),
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

// ---------------------------------------------------------------------------
// Stats Cards
// ---------------------------------------------------------------------------
function StatsCards({ stats }) {
  const cards = [
    {
      label: "Total clients",
      value: stats.total_clients,
      icon: Users,
      color: "text-zinc-300",
      bg: "bg-zinc-800",
    },
    {
      label: "Avec email",
      value: stats.clients_with_email,
      icon: Mail,
      color: "text-emerald-400",
      bg: "bg-emerald-500/10",
    },
    {
      label: "Avec telephone",
      value: stats.clients_with_phone,
      icon: Phone,
      color: "text-blue-400",
      bg: "bg-blue-500/10",
    },
    {
      label: "Analyses faites",
      value: stats.clients_analyzed,
      icon: Bot,
      color: "text-violet-400",
      bg: "bg-violet-500/10",
    },
    {
      label: "Avec GIR (MPA)",
      value: stats.clients_with_gir,
      icon: FileText,
      color: "text-amber-400",
      bg: "bg-amber-500/10",
    },
    {
      label: "Taux completion",
      value: `${stats.completion_rate}%`,
      icon: TrendingUp,
      color: "text-emerald-400",
      bg: "bg-emerald-500/10",
    },
  ];

  return (
    <div
      data-testid="compliance-stats-grid"
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
    >
      {cards.map((c) => (
        <Card key={c.label} className={`${c.bg} border-zinc-700/50`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <c.icon className={`w-4 h-4 ${c.color}`} />
              <span className="text-xs text-zinc-400">{c.label}</span>
            </div>
            <p className={`text-2xl font-bold ${c.color}`}>{c.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single Client Analysis Result
// ---------------------------------------------------------------------------
function AnalysisResult({ result, expanded, onToggle }) {
  if (!result) return null;

  return (
    <Card
      data-testid={`analysis-result-${result.client_id || "zip"}`}
      className={`border-zinc-700/50 ${result.success ? "bg-emerald-500/5" : "bg-red-500/5"}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {result.success ? (
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            ) : (
              <XCircle className="w-5 h-5 text-red-400" />
            )}
            <div>
              <p className="font-medium text-white">
                {result.client_name || "Client"}
              </p>
              <p className="text-xs text-zinc-400">
                {result.success
                  ? `${result.files_analyzed || 0} fichiers analyses - ${(result.fields_updated_client || []).length} champs client mis a jour`
                  : result.error}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            data-testid="toggle-result-details"
          >
            {expanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </Button>
        </div>

        {expanded && result.success && (
          <div className="mt-4 space-y-3 border-t border-zinc-700/50 pt-3">
            {result.fields_updated_client?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-zinc-400 mb-1">
                  Champs client mis a jour :
                </p>
                <div className="flex flex-wrap gap-1">
                  {result.fields_updated_client.map((f) => (
                    <Badge
                      key={f}
                      className="bg-emerald-500/20 text-emerald-400 text-xs"
                    >
                      {f}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {result.fields_updated_dossier?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-zinc-400 mb-1">
                  Champs dossier mis a jour :
                </p>
                <div className="flex flex-wrap gap-1">
                  {result.fields_updated_dossier.map((f) => (
                    <Badge
                      key={f}
                      className="bg-blue-500/20 text-blue-400 text-xs"
                    >
                      {f}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {result.installateur && (result.installateur.entreprise || result.installateur.nom) && (
              <div className={`p-2 rounded border text-sm ${result.installateur.created ? "bg-amber-500/10 border-amber-500/30" : "bg-zinc-800 border-zinc-700"}`}>
                <span className="text-zinc-400">Installateur : </span>
                <span className="text-white font-medium">{getInstallateurDisplayName(result.installateur)}</span>
                {result.installateur.siret && <span className="text-zinc-500 ml-2">SIRET: {result.installateur.siret}</span>}
                {result.installateur.created ? (
                  <Badge className="ml-2 bg-amber-500/20 text-amber-400 text-xs">Créé en Operation</Badge>
                ) : (
                  <Badge className="ml-2 bg-zinc-700 text-zinc-400 text-xs">Déjà existant</Badge>
                )}
              </div>
            )}
            {result.extracted_data && (
              <details className="text-xs">
                <summary className="text-zinc-400 cursor-pointer hover:text-zinc-300">
                  Données extraites (JSON)
                </summary>
                <pre className="mt-2 p-3 bg-zinc-900/50 rounded text-zinc-300 overflow-auto max-h-60">
                  {JSON.stringify(result.extracted_data, null, 2)}
                </pre>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Enhanced Bulk Progress with current dossier, ETA, abort
// ---------------------------------------------------------------------------
function BulkProgressPanel({ status, onAbort }) {
  if (!status || status.status === "not_found") return null;

  const pct = status.total > 0 ? Math.round((status.processed / status.total) * 100) : 0;
  const isRunning = status.status === "running";
  const isAborted = status.status === "aborted";

  // Estimate remaining time
  let etaText = "";
  if (isRunning && status.processed > 0 && status.started_at) {
    const elapsed = (Date.now() - new Date(status.started_at).getTime()) / 1000;
    const perItem = elapsed / status.processed;
    const remaining = perItem * (status.total - status.processed);
    if (remaining > 60) etaText = `~${Math.ceil(remaining / 60)} min restantes`;
    else etaText = `~${Math.ceil(remaining)}s restantes`;
  }

  return (
    <Card data-testid="bulk-progress-panel" className="border-violet-500/30 bg-violet-500/5">
      <CardContent className="p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-violet-400" />
            <span className="font-medium text-white">Analyse en masse</span>
            {status.mode && <Badge className="bg-zinc-700 text-zinc-300 text-[10px]">{status.mode === "all" ? "Tous" : "Incomplets"}</Badge>}
          </div>
          <div className="flex items-center gap-2">
            <Badge className={
              isRunning ? "bg-amber-500/20 text-amber-400" :
              isAborted ? "bg-[var(--oh-stat-orange-bg)] text-[var(--oh-stat-orange-text)]" :
              status.status === "completed" ? "bg-emerald-500/20 text-emerald-400" :
              "bg-red-500/20 text-red-400"
            }>
              {isRunning ? "En cours..." : isAborted ? "Arrêté" : status.status === "completed" ? "Terminé" : "Erreur"}
            </Badge>
            {isRunning && onAbort && (
              <Button size="sm" variant="outline" onClick={onAbort} className="h-7 text-xs border-red-600 text-red-400 hover:bg-red-600/20" data-testid="abort-batch-btn">
                <StopCircle className="w-3.5 h-3.5 mr-1" /> Arrêter
              </Button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="space-y-1.5">
          <Progress value={pct} className="h-2.5" />
          <div className="flex items-center justify-between text-xs text-zinc-400">
            <span>{status.processed}/{status.total} dossiers traités</span>
            {etaText && <span className="flex items-center gap-1"><Timer className="w-3 h-3" />{etaText}</span>}
          </div>
        </div>

        {/* Current dossier */}
        {isRunning && status.current_dossier && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-violet-500/10 border border-violet-500/20">
            <Loader2 className="w-4 h-4 animate-spin text-violet-400 shrink-0" />
            <span className="text-sm text-violet-300">
              En cours : <strong className="font-mono">{status.current_dossier.reference}</strong>
              {status.current_dossier.client_nom && <span className="text-violet-400/70 ml-1">({status.current_dossier.client_nom})</span>}
            </span>
          </div>
        )}

        {/* Stats live */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-center">
          <div className="p-2 rounded-lg bg-zinc-800/50">
            <p className="text-lg font-bold text-white">{status.processed || 0}</p>
            <p className="text-[10px] text-zinc-400">Traités</p>
          </div>
          <div className="p-2 rounded-lg bg-emerald-500/10">
            <p className="text-lg font-bold text-emerald-400">{status.success || 0}</p>
            <p className="text-[10px] text-zinc-400">Complétés</p>
          </div>
          <div className="p-2 rounded-lg bg-amber-500/10">
            <p className="text-lg font-bold text-amber-400">{status.tickets || 0}</p>
            <p className="text-[10px] text-zinc-400">Tickets</p>
          </div>
          <div className="p-2 rounded-lg bg-red-500/10">
            <p className="text-lg font-bold text-red-400">{status.failed || 0}</p>
            <p className="text-[10px] text-zinc-400">Erreurs</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function ComplianceAgentPage({ tenantId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadingZip, setUploadingZip] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [expandedResult, setExpandedResult] = useState(true);
  const [bulkTaskId, setBulkTaskId] = useState(null);
  const [bulkStatus, setBulkStatus] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkMode, setBulkMode] = useState("incomplete"); // "all" | "incomplete"
  const [bulkBatchSize, setBulkBatchSize] = useState(50);
  const [batchHistory, setBatchHistory] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [activeMode, setActiveMode] = useState("conversationnel"); // "conversationnel" | "analyse" | "dates"
  const [dateSyncPreview, setDateSyncPreview] = useState(null);
  const [dateSyncTaskId, setDateSyncTaskId] = useState(null);
  const [dateSyncStatus, setDateSyncStatus] = useState(null);
  const [dateSyncRunning, setDateSyncRunning] = useState(false);
  // S3 — Enrichissement depuis documents (batch-fill-from-docs), flux 2 étapes
  const [batchFillTask, setBatchFillTask] = useState(null); // { id, dryRun }
  const [batchFillPreview, setBatchFillPreview] = useState(null); // résultat dry_run
  const [batchFillStatus, setBatchFillStatus] = useState(null);   // résultat run réel
  const [batchFillRunning, setBatchFillRunning] = useState(false);

  const headers = getAuthHeaders(tenantId);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/conformite/agent/stats`, {
        headers,
      });
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (e) {
      console.error("Stats load error:", e);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // Load batch history — DOIT être déclaré AVANT tout useEffect qui l'utilise
  const loadBatchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/conformite/agent/batch-completude/history`, { headers });
      if (res.ok) setBatchHistory(await res.json());
    } catch {}
  }, [tenantId]);

  // Load clients for search
  const searchClients = useCallback(
    async (q) => {
      if (!q || q.length < 2) {
        setClients([]);
        return;
      }
      try {
        const res = await fetch(
          `${API}/api/metier/clients?search=${encodeURIComponent(q)}&limit=20`,
          { headers }
        );
        if (res.ok) {
          const data = await res.json();
          setClients(data.clients || data || []);
        }
      } catch (e) {
        console.error("Search error:", e);
      }
    },
    [tenantId]
  );

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Load batch history when on analyse tab
  useEffect(() => {
    if (activeMode === "analyse") loadBatchHistory();
  }, [activeMode, loadBatchHistory]);

  // Poll bulk status (batch-completude)
  useEffect(() => {
    if (!bulkTaskId) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(
          `${API}/api/conformite/agent/batch-completude/status/${bulkTaskId}`,
          { headers }
        );
        if (res.ok) {
          const data = await res.json();
          setBulkStatus(data);
          if (data.status !== "running") {
            clearInterval(interval);
            loadStats();
            loadBatchHistory();
          }
        }
      } catch (e) {
        console.error("Batch status error:", e);
      }
    }, 2500);
    return () => clearInterval(interval);
  }, [bulkTaskId]);

  // Analyze single client
  const handleAnalyzeClient = async (clientId) => {
    setAnalyzing(true);
    setLastResult(null);
    try {
      const res = await fetch(
        `${API}/api/conformite/agent/analyze/${clientId}`,
        {
          method: "POST",
          headers: {
            ...enteteTenant(tenantId),
            ...(localStorage.getItem("session_token") && {
              Authorization: `Bearer ${localStorage.getItem("session_token")}`,
            }),
          },
        }
      );
      const text = await res.text();
      const data = JSON.parse(text);
      setLastResult(data);
      setExpandedResult(true);
      loadStats();
    } catch (e) {
      setLastResult({ success: false, error: e.message });
    } finally {
      setAnalyzing(false);
    }
  };

  // Upload zip for client
  const handleUploadZip = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedClient) return;

    setUploadingZip(true);
    setLastResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const token = localStorage.getItem("session_token");
      const res = await fetch(
        `${API}/api/conformite/agent/analyze-zip/${selectedClient.id}`,
        {
          method: "POST",
          headers: {
            ...enteteTenant(tenantId),
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: formData,
        }
      );
      const data = await res.json();
      setLastResult(data);
      setExpandedResult(true);
      loadStats();
    } catch (e) {
      setLastResult({ success: false, error: e.message });
    } finally {
      setUploadingZip(false);
    }
  };

  // Start bulk via batch-completude
  const handleBulkAnalyze = async () => {
    setShowBulkModal(false);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const res = await fetch(`${API}/api/conformite/agent/batch-completude`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          mode: bulkMode,
          batch_size: bulkBatchSize,
          threshold: 80,
          user_email: user.email || "admin",
        }),
      });
      const data = await res.json();
      setBulkTaskId(data.task_id);
      setBulkStatus({ status: "running", processed: 0, total: 0, success: 0, failed: 0, tickets: 0, started_at: new Date().toISOString(), mode: bulkMode });
    } catch (e) {
      console.error("Batch start error:", e);
    }
  };

  // Abort batch
  const handleAbortBatch = async () => {
    if (!bulkTaskId) return;
    try {
      await fetch(`${API}/api/conformite/agent/batch-completude/abort/${bulkTaskId}`, { method: "POST", headers });
    } catch (e) { console.error("Abort error:", e); }
  };

  // ── Date Sync functions ──
  const loadDateSyncPreview = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/conformite/agent/date-sync/preview?tenant_id=${tenantId}`, { headers });
      if (res.ok) setDateSyncPreview(await res.json());
    } catch (e) { console.error("Date sync preview error:", e); }
  }, [tenantId, headers]);

  const startDateSync = async () => {
    setDateSyncRunning(true);
    setDateSyncStatus(null);
    try {
      const res = await fetch(`${API}/api/conformite/agent/date-sync/run?tenant_id=${tenantId}&batch_size=10&pause_seconds=30`, {
        method: "POST", headers,
      });
      if (res.ok) {
        const data = await res.json();
        setDateSyncTaskId(data.task_id);
      }
    } catch (e) { console.error("Date sync start error:", e); }
  };

  // Poll date sync status
  useEffect(() => {
    if (!dateSyncTaskId) return;
    const poll = setInterval(async () => {
      try {
        const res = await fetch(`${API}/api/conformite/agent/date-sync/status/${dateSyncTaskId}`, { headers });
        if (res.ok) {
          const data = await res.json();
          setDateSyncStatus(data);
          if (data.status === "completed" || data.status === "error") {
            clearInterval(poll);
            setDateSyncRunning(false);
            loadDateSyncPreview();
          }
        }
      } catch {}
    }, 5000);
    return () => clearInterval(poll);
  }, [dateSyncTaskId]);

  // Load preview when switching to dates tab
  useEffect(() => {
    if (activeMode === "dates" && !dateSyncPreview) {
      loadDateSyncPreview();
    }
  }, [activeMode]);

  // ── S3 — Enrichissement depuis documents (batch-fill-from-docs) ──
  // Flux 2 étapes : Aperçu (dry_run, aucune écriture) puis Lancer.
  const startBatchFill = async (dryRun) => {
    setBatchFillRunning(true);
    if (dryRun) { setBatchFillPreview(null); setBatchFillStatus(null); }
    else setBatchFillStatus(null);
    try {
      const res = await fetch(`${API}/api/conformite/agent/batch-fill-from-docs`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ only_incomplete: true, dry_run: dryRun, batch_size: 200 }),
      });
      if (res.ok) {
        const data = await res.json();
        setBatchFillTask({ id: data.task_id, dryRun });
      } else {
        setBatchFillRunning(false);
      }
    } catch (e) {
      console.error("Batch fill error:", e);
      setBatchFillRunning(false);
    }
  };

  // Poll batch-fill status
  useEffect(() => {
    if (!batchFillTask) return;
    const poll = setInterval(async () => {
      try {
        const res = await fetch(`${API}/api/conformite/agent/batch-fill-from-docs/status/${batchFillTask.id}`, { headers });
        if (res.ok) {
          const data = await res.json();
          if (batchFillTask.dryRun) setBatchFillPreview(data);
          else setBatchFillStatus(data);
          if (["completed", "error", "aborted"].includes(data.status)) {
            clearInterval(poll);
            setBatchFillRunning(false);
          }
        }
      } catch {}
    }, 2500);
    return () => clearInterval(poll);
  }, [batchFillTask]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="compliance-agent-page" className="space-y-6 p-1">
      {/* Header with Mode Toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Bot className="w-7 h-7 text-violet-400" />
            Joey (Conformité)
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            {activeMode === "conversationnel"
              ? "Chat libre avec l'agent IA — posez des questions, donnez des instructions"
              : "Analyse unitaire ou en masse des dossiers clients"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-zinc-800/50 border border-zinc-700/50 rounded-lg p-0.5">
            <button
              data-testid="mode-conversationnel-btn"
              onClick={() => setActiveMode("conversationnel")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all ${
                activeMode === "conversationnel"
                  ? "bg-violet-600 text-white shadow"
                  : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Conversationnel
            </button>
            <button
              data-testid="mode-analyse-btn"
              onClick={() => setActiveMode("analyse")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all ${
                activeMode === "analyse"
                  ? "bg-blue-600 text-white shadow"
                  : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <ScanSearch className="w-3.5 h-3.5" />
              Analyse
            </button>
            <button
              data-testid="mode-dates-btn"
              onClick={() => setActiveMode("dates")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all ${
                activeMode === "dates"
                  ? "bg-amber-600 text-white shadow"
                  : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <CalendarClock className="w-3.5 h-3.5" />
              Dates
            </button>
          </div>
          {activeMode === "analyse" && (
            <Button
              variant="outline"
              size="sm"
              onClick={loadStats}
              data-testid="refresh-stats-btn"
              className="border-zinc-700 text-zinc-300"
            >
              <RefreshCw className="w-4 h-4 mr-1" /> Actualiser
            </Button>
          )}
        </div>
      </div>

      {/* Mode Conversationnel - Chat Libre */}
      {activeMode === "conversationnel" && (
        <ChatLibrePanel tenantId={tenantId} />
      )}

      {/* Mode Analyse (Unitaire + Bulk fusionnés) */}
      {activeMode === "analyse" && (
        <>
          {/* Stats */}
          {stats && <StatsCards stats={stats} />}

          {/* Analyse guidée (conversation) */}
          <ConversationPanel tenantId={tenantId} />

      {/* Actions Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Single Client Analysis */}
        <Card className="border-zinc-700/50 bg-zinc-800/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Search className="w-4 h-4 text-blue-400" />
              Analyser un client
            </CardTitle>
            <CardDescription>
              Recherchez un client et lancez l'extraction depuis son dossier Zoho
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              data-testid="client-search-input"
              placeholder="Rechercher par nom..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                searchClients(e.target.value);
              }}
              className="bg-zinc-900/50 border-zinc-700 text-white"
            />

            {clients.length > 0 && (
              <div className="max-h-40 overflow-y-auto space-y-1 border border-zinc-700/50 rounded p-2">
                {clients.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`client-option-${c.id}`}
                    onClick={() => {
                      setSelectedClient(c);
                      setSearchQuery(`${c.nom} ${c.prenom || ""}`);
                      setClients([]);
                    }}
                    className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-zinc-700/50 transition ${
                      selectedClient?.id === c.id
                        ? "bg-[var(--oh-stat-purple-bg)] text-[var(--oh-stat-purple-text)]"
                        : "text-zinc-300"
                    }`}
                  >
                    <span className="font-medium">
                      {c.nom} {c.prenom}
                    </span>
                    {c.email && (
                      <span className="text-xs text-zinc-500 ml-2">
                        {c.email}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {selectedClient && (
              <div className="flex items-center gap-2 p-2 bg-violet-500/10 border border-violet-500/30 rounded">
                <FolderOpen className="w-4 h-4 text-violet-400" />
                <span className="text-sm text-violet-300">
                  {selectedClient.nom} {selectedClient.prenom}
                </span>
                <div className="ml-auto flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleAnalyzeClient(selectedClient.id)}
                    disabled={analyzing}
                    data-testid="analyze-zoho-btn"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {analyzing ? (
                      <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                      <Play className="w-3 h-3 mr-1" />
                    )}
                    Zoho
                  </Button>
                  <label>
                    <input
                      type="file"
                      accept=".zip"
                      className="hidden"
                      onChange={handleUploadZip}
                      data-testid="upload-zip-input"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      asChild
                      disabled={uploadingZip}
                      className="border-zinc-600 cursor-pointer"
                    >
                      <span>
                        {uploadingZip ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <Upload className="w-3 h-3 mr-1" />
                        )}
                        ZIP
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bulk Analysis Card */}
        <Card className="border-zinc-700/50 bg-zinc-800/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-white flex items-center gap-2">
              <Layers className="w-4 h-4 text-violet-400" />
              Analyse en masse
            </CardTitle>
            <CardDescription>
              Analyser automatiquement les dossiers avec données manquantes via Zoho + Claude
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              onClick={() => setShowBulkModal(true)}
              disabled={bulkStatus?.status === "running"}
              data-testid="open-bulk-modal-btn"
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {bulkStatus?.status === "running" ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Analyse en cours...</>
              ) : (
                <><Layers className="w-4 h-4 mr-2" /> Analyser en masse</>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Progress Panel */}
      {bulkStatus && <BulkProgressPanel status={bulkStatus} onAbort={handleAbortBatch} />}

      {/* Zoho Structure */}
      <Card className="border-zinc-700/50 bg-zinc-800/50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-white flex items-center gap-2">
                <FolderOpen className="w-4 h-4 text-amber-400" />
                Structure Zoho WorkDrive
              </p>
              <p className="text-xs text-zinc-400 mt-1">
                Créer l'arborescence miroir : un dossier par client dans COMMERCIAL/Dossiers/OmniHub/
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                data-testid="check-zoho-btn"
                className="border-zinc-600"
                onClick={async () => {
                  try {
                    const res = await fetch(`${API}/api/conformite/agent/zoho-folders`, { headers });
                    if (res.ok) {
                      const data = await res.json();
                      setLastResult({ success: true, client_name: "Zoho Folders", extracted_data: data, fields_updated_client: [], fields_updated_dossier: [] });
                      setExpandedResult(true);
                    }
                  } catch (e) { console.error(e); }
                }}
              >
                <Search className="w-3 h-3 mr-1" /> Verifier
              </Button>
              <Button
                size="sm"
                data-testid="create-zoho-mirror-btn"
                className="bg-amber-600 hover:bg-amber-700"
                onClick={async () => {
                  if (!window.confirm(`Créer 205 dossiers dans Zoho WorkDrive ?`)) return;
                  try {
                    const res = await fetch(`${API}/api/conformite/agent/zoho-mirror`, { method: "POST", headers });
                    if (res.ok) {
                      const data = await res.json();
                      setLastResult({ success: true, client_name: `Miroir Zoho : ${data.folders_created} dossiers crees`, extracted_data: data, fields_updated_client: [], fields_updated_dossier: [] });
                      setExpandedResult(true);
                    }
                  } catch (e) { console.error(e); }
                }}
              >
                <FolderOpen className="w-3 h-3 mr-1" /> Créer miroir
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Last Result */}
      {lastResult && (
        <div>
          <h3 className="text-sm font-medium text-zinc-400 mb-2">
            Dernier resultat
          </h3>
          <AnalysisResult
            result={lastResult}
            expanded={expandedResult}
            onToggle={() => setExpandedResult(!expandedResult)}
          />
        </div>
      )}

      {/* Batch History */}
      {batchHistory.length > 0 && (
        <Card className="border-zinc-700/50 bg-zinc-800/50" data-testid="batch-history-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-white flex items-center gap-2">
              <History className="w-4 h-4 text-zinc-400" />
              Runs batch récents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {batchHistory.slice(0, 10).map((run, i) => (
                <div key={run.task_id || i} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-zinc-700/30 transition-colors" style={{ borderBottom: "1px solid var(--oh-border, rgba(255,255,255,0.05))" }}>
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${run.status === "completed" ? "bg-emerald-400" : run.status === "aborted" ? "bg-orange-400" : "bg-red-400"}`} />
                    <div>
                      <p className="text-sm text-white">
                        {run.total || 0} dossiers — <span className="text-emerald-400">{run.success || 0} OK</span>
                        {(run.failed || 0) > 0 && <span className="text-red-400 ml-1"> {run.failed} erreurs</span>}
                      </p>
                      <p className="text-[11px] text-zinc-500">
                        {run.created_at ? new Date(run.created_at).toLocaleString("fr-FR") : "—"}
                        {run.started_by && <span className="ml-2">par {run.started_by}</span>}
                        {run.mode && <span className="ml-2">({run.mode === "all" ? "tous" : "incomplets"})</span>}
                      </p>
                    </div>
                  </div>
                  <Badge className={`text-[10px] ${run.status === "completed" ? "bg-emerald-500/15 text-emerald-400" : run.status === "aborted" ? "bg-[var(--oh-stat-orange-bg)] text-[var(--oh-stat-orange-text)]" : "bg-red-500/15 text-red-400"}`}>
                    {run.status === "completed" ? "Terminé" : run.status === "aborted" ? "Arrêté" : "Erreur"}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
        </>
      )}

      {/* Bulk Modal */}
      <Dialog open={showBulkModal} onOpenChange={setShowBulkModal}>
        <DialogContent style={{ background: "var(--oh-bg-surface, #1a1a2e)", borderColor: "var(--oh-border, rgba(255,255,255,0.1))", maxWidth: "460px" }} data-testid="bulk-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-400" /> Analyser en masse
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Mode */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-zinc-400">Mode d'analyse</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  onClick={() => setBulkMode("incomplete")}
                  className={`p-3 rounded-lg border text-left transition-all ${bulkMode === "incomplete" ? "border-blue-500 bg-blue-500/10" : "border-zinc-700 hover:border-zinc-600"}`}
                  data-testid="bulk-mode-incomplete"
                >
                  <p className="text-sm font-medium text-white">Incomplets uniquement</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Dossiers avec complétude &lt; 80%</p>
                </button>
                <button
                  onClick={() => setBulkMode("all")}
                  className={`p-3 rounded-lg border text-left transition-all ${bulkMode === "all" ? "border-blue-500 bg-blue-500/10" : "border-zinc-700 hover:border-zinc-600"}`}
                  data-testid="bulk-mode-all"
                >
                  <p className="text-sm font-medium text-white">Tous les dossiers</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Re-analyser l'ensemble</p>
                </button>
              </div>
            </div>

            {/* Batch size */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-zinc-400">Nombre de dossiers</p>
              <div className="flex gap-2">
                {[10, 25, 50, 100, 200].map(n => (
                  <button key={n} onClick={() => setBulkBatchSize(n)}
                    className={`flex-1 py-2 rounded-md text-sm font-medium transition-all ${bulkBatchSize === n ? "bg-blue-600 text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"}`}
                    data-testid={`bulk-size-${n}`}>
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {/* Summary */}
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-300 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                Lancer sur <strong>{bulkBatchSize}</strong> dossiers ({bulkMode === "all" ? "tous" : "incomplets"}).
                Durée estimée : ~{Math.ceil(bulkBatchSize * 5 / 60)} min.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowBulkModal(false)}>Annuler</Button>
            <Button onClick={handleBulkAnalyze} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-bulk-btn">
              <Play className="w-4 h-4 mr-1.5" /> Démarrer l'analyse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mode Dates — Sync dates réelles depuis Zoho */}
      {activeMode === "dates" && (
        <div className="space-y-4">
          {/* Preview Card */}
          <Card className="bg-white dark:bg-zinc-900/60 border-slate-200 dark:border-white/5">
            <CardHeader>
              <CardTitle className="text-slate-800 dark:text-white text-base flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-amber-500" />
                Synchronisation des dates reelles
              </CardTitle>
              <CardDescription className="text-slate-500 dark:text-zinc-400">
                Extrait les dates des documents Zoho (contrats AMO, lettres ANAH, notifications...) et met a jour les dossiers.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Preview Stats */}
              {dateSyncPreview && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-slate-50 dark:bg-zinc-800/50 border border-slate-200 dark:border-white/5 rounded-lg p-3">
                    <p className="text-xs text-slate-500 dark:text-zinc-400">Dossiers avec Zoho</p>
                    <p className="text-xl font-bold text-slate-800 dark:text-white">{dateSyncPreview.total_dossiers_with_zoho}</p>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg p-3">
                    <p className="text-xs text-amber-600 dark:text-amber-400">Sans date creation</p>
                    <p className="text-xl font-bold text-amber-700 dark:text-amber-300">{dateSyncPreview.without_date_creation}</p>
                  </div>
                  <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-lg p-3">
                    <p className="text-xs text-emerald-600 dark:text-emerald-400">Avec date creation</p>
                    <p className="text-xl font-bold text-emerald-700 dark:text-emerald-300">{dateSyncPreview.with_date_creation}</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg p-3">
                    <p className="text-xs text-red-600 dark:text-red-400">Dates manquantes</p>
                    <p className="text-xl font-bold text-red-700 dark:text-red-300">{dateSyncPreview.missing_dates}</p>
                  </div>
                </div>
              )}

              {/* Strategy info */}
              <div className="bg-slate-50 dark:bg-zinc-800/30 border border-slate-200 dark:border-white/5 rounded-lg p-4">
                <h4 className="text-sm font-medium text-slate-700 dark:text-zinc-300 mb-2">Strategie d'extraction</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                  <div className="flex items-start gap-2">
                    <span className="bg-emerald-500 text-white rounded px-1.5 py-0.5 text-[10px] font-bold shrink-0">1</span>
                    <span className="text-slate-600 dark:text-zinc-400">Nom du fichier (regex, gratuit)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="bg-amber-500 text-white rounded px-1.5 py-0.5 text-[10px] font-bold shrink-0">2</span>
                    <span className="text-slate-600 dark:text-zinc-400">Contenu PDF + LLM (GPT-4o)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="bg-slate-400 text-white rounded px-1.5 py-0.5 text-[10px] font-bold shrink-0">3</span>
                    <span className="text-slate-600 dark:text-zinc-400">Date upload Zoho (fallback)</span>
                  </div>
                </div>
                <p className="text-xs text-slate-500 dark:text-zinc-500 mt-2">
                  Batch de 10 dossiers avec pause 30s. Les dates existantes ne sont jamais ecrasees.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                <Button
                  data-testid="date-sync-run-btn"
                  onClick={startDateSync}
                  disabled={dateSyncRunning}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {dateSyncRunning ? (
                    <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> En cours...</>
                  ) : (
                    <><Play className="w-4 h-4 mr-1.5" /> Lancer la synchronisation</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadDateSyncPreview}
                  className="border-slate-200 dark:border-zinc-700 text-slate-600 dark:text-zinc-300"
                >
                  <RefreshCw className="w-4 h-4 mr-1" /> Actualiser
                </Button>
              </div>

              {/* Progress / Status */}
              {dateSyncStatus && (
                <div className="bg-slate-50 dark:bg-zinc-800/50 border border-slate-200 dark:border-white/5 rounded-lg p-4 space-y-3">
                  {/* Error message */}
                  {dateSyncStatus.error && (
                    <div className="flex items-center gap-2 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg p-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
                      <p className="text-sm text-red-700 dark:text-red-400">{dateSyncStatus.error}</p>
                    </div>
                  )}
                  {!dateSyncStatus.error && (
                    <>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700 dark:text-zinc-300 flex items-center gap-2">
                      {dateSyncStatus.status === "running" && <Loader2 className="w-4 h-4 animate-spin text-amber-500" />}
                      {dateSyncStatus.status === "completed" && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                      {dateSyncStatus.status === "error" && <XCircle className="w-4 h-4 text-red-500" />}
                      {dateSyncStatus.status === "running" ? "Synchronisation en cours..." :
                       dateSyncStatus.status === "completed" ? "Synchronisation terminee" : "Erreur"}
                    </span>
                    {dateSyncStatus.total_dossiers > 0 && (
                      <span className="text-xs text-slate-500 dark:text-zinc-400">
                        {dateSyncStatus.processed || 0} / {dateSyncStatus.total_dossiers}
                      </span>
                    )}
                  </div>

                  {dateSyncStatus.total_dossiers > 0 && (
                    <Progress
                      value={((dateSyncStatus.processed || 0) / dateSyncStatus.total_dossiers) * 100}
                      className="h-2"
                    />
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold text-slate-800 dark:text-white">{dateSyncStatus.processed || 0}</p>
                      <p className="text-[10px] text-slate-500 dark:text-zinc-400">Traites</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{dateSyncStatus.updated || 0}</p>
                      <p className="text-[10px] text-slate-500 dark:text-zinc-400">Mis a jour</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-red-600 dark:text-red-400">{dateSyncStatus.errors || 0}</p>
                      <p className="text-[10px] text-slate-500 dark:text-zinc-400">Erreurs</p>
                    </div>
                  </div>

                  {/* Details of completed sync */}
                  {dateSyncStatus.status === "completed" && dateSyncStatus.details && (
                    <div className="max-h-64 overflow-y-auto space-y-1 mt-2 border-t border-slate-100 dark:border-white/5 pt-2">
                      {dateSyncStatus.details.filter(d => Object.keys(d.dates_updated || {}).length > 0).map((d, i) => (
                        <div key={i} className="flex items-center justify-between text-xs py-1 px-2 bg-emerald-50 dark:bg-emerald-500/5 rounded">
                          <span className="font-mono text-slate-700 dark:text-zinc-300">{d.référence}</span>
                          <div className="flex gap-1 flex-wrap justify-end">
                            {Object.entries(d.dates_updated || {}).map(([field, val]) => (
                              <Badge key={field} className="bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 text-[9px]">
                                {field.replace("date_", "")}: {val}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* S3 — Enrichissement depuis les documents (batch-fill-from-docs), flux 2 étapes */}
          <Card className="bg-white dark:bg-zinc-900/60 border-slate-200 dark:border-white/5" data-testid="batch-fill-card">
            <CardHeader>
              <CardTitle className="text-slate-800 dark:text-white text-base flex items-center gap-2">
                <FileText className="w-5 h-5 text-violet-500" />
                Enrichissement depuis les documents
              </CardTitle>
              <CardDescription className="text-slate-500 dark:text-zinc-400">
                Complète les dossiers incomplets à partir des documents déjà indexés
                (montant travaux, RFR, flags, checklist). N'écrase jamais une saisie manuelle.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Attente réaliste (décision CTO) */}
              <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg p-3 text-xs text-amber-700 dark:text-amber-300">
                Attente réaliste : enrichit <strong>~26 dossiers</strong> (montant_travaux, RFR,
                flags, checklist). Les <strong>32 dates d'octroi ne sont PAS remplies ici</strong> —
                seuls ~3 dossiers ont une lettre d'octroi indexée.
              </div>

              {/* Résultat Aperçu (dry_run) */}
              {batchFillPreview && (
                <div className="bg-slate-50 dark:bg-zinc-800/50 border border-slate-200 dark:border-white/5 rounded-lg p-4 space-y-2" data-testid="batch-fill-preview">
                  <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-zinc-300">
                    {batchFillPreview.status === "running"
                      ? <><Loader2 className="w-4 h-4 animate-spin text-violet-500" /> Aperçu en cours…</>
                      : <><CheckCircle className="w-4 h-4 text-emerald-500" /> Aperçu (aucune écriture)</>}
                  </div>
                  <p className="text-sm text-slate-600 dark:text-zinc-400">
                    <strong className="text-slate-800 dark:text-white">{batchFillPreview.total ?? 0}</strong> dossier(s) candidat(s) seront examinés — <strong>rien n'a été modifié</strong>. Le nombre réellement enrichi est plus faible (voir l'attente réaliste ci-dessus).
                  </p>
                </div>
              )}

              {/* Résultat Lancer (run réel) */}
              {batchFillStatus && (
                <div className="bg-slate-50 dark:bg-zinc-800/50 border border-slate-200 dark:border-white/5 rounded-lg p-4 space-y-3" data-testid="batch-fill-status">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700 dark:text-zinc-300 flex items-center gap-2">
                      {batchFillStatus.status === "running" && <Loader2 className="w-4 h-4 animate-spin text-violet-500" />}
                      {batchFillStatus.status === "completed" && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                      {(batchFillStatus.status === "error" || batchFillStatus.status === "aborted") && <XCircle className="w-4 h-4 text-red-500" />}
                      {batchFillStatus.status === "running" ? "Enrichissement en cours…" :
                       batchFillStatus.status === "completed" ? "Enrichissement terminé" :
                       batchFillStatus.status === "aborted" ? "Interrompu" : "Erreur"}
                    </span>
                    {batchFillStatus.total > 0 && (
                      <span className="text-xs text-slate-500 dark:text-zinc-400">
                        {batchFillStatus.processed || 0} / {batchFillStatus.total}
                      </span>
                    )}
                  </div>
                  {batchFillStatus.total > 0 && (
                    <Progress value={((batchFillStatus.processed || 0) / batchFillStatus.total) * 100} className="h-2" />
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{batchFillStatus.success || 0}</p>
                      <p className="text-[10px] text-slate-500 dark:text-zinc-400">Enrichis</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-red-600 dark:text-red-400">{batchFillStatus.failed || 0}</p>
                      <p className="text-[10px] text-slate-500 dark:text-zinc-400">Échecs</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-slate-800 dark:text-white">{batchFillStatus.total || 0}</p>
                      <p className="text-[10px] text-slate-500 dark:text-zinc-400">Total</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Boutons 2 étapes : Aperçu puis Lancer */}
              <div className="flex items-center gap-3">
                <Button
                  variant="outline" size="sm"
                  data-testid="batch-fill-preview-btn"
                  onClick={() => startBatchFill(true)}
                  disabled={batchFillRunning}
                  className="border-slate-200 dark:border-zinc-700 text-slate-600 dark:text-zinc-300"
                >
                  {batchFillRunning && batchFillTask?.dryRun
                    ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Aperçu…</>
                    : <><ScanSearch className="w-4 h-4 mr-1.5" /> Aperçu</>}
                </Button>
                <Button
                  data-testid="batch-fill-run-btn"
                  onClick={() => startBatchFill(false)}
                  disabled={batchFillRunning || !batchFillPreview}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  title={!batchFillPreview ? "Lancer d'abord un Aperçu" : "Lancer l'enrichissement"}
                >
                  {batchFillRunning && !batchFillTask?.dryRun
                    ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> En cours…</>
                    : <><Play className="w-4 h-4 mr-1.5" /> Lancer</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
